import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { api } from '../lib/api-client';
import { syncFireAndForget } from '../lib/sync';

function currentTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

interface NotificationPrefsState {
  reminderEnabled: boolean;
  /** "HH:mm" local wall-clock, or null when unset. */
  reminderTime: string | null;
  /** IANA zone, captured automatically when prefs change. */
  timezone: string | null;
  /** Local-only routing flag — true once the push opt-in step has been shown. */
  optInPromptSeen: boolean;

  setReminderEnabled: (enabled: boolean) => void;
  setReminderTime: (time: string) => void;
  markOptInSeen: () => void;
  hydrate: (prefs: {
    reminderEnabled: boolean;
    reminderTime: string | null;
    timezone: string | null;
  }) => void;
  reset: () => void;
}

const initial = {
  reminderEnabled: false,
  reminderTime: null as string | null,
  timezone: null as string | null,
  optInPromptSeen: false,
};

// Default reminder time offered when a user first enables reminders.
const DEFAULT_REMINDER_TIME = '20:00';

export const useNotificationPrefsStore = create<NotificationPrefsState>()(
  persist(
    (set, get) => ({
      ...initial,

      setReminderEnabled: (reminderEnabled) => {
        const reminderTime = get().reminderTime ?? (reminderEnabled ? DEFAULT_REMINDER_TIME : null);
        const timezone = currentTimezone();
        set({ reminderEnabled, reminderTime, timezone });
        syncFireAndForget(() =>
          api.me.updateNotificationPrefs({ reminderEnabled, reminderTime, timezone }),
        );
      },

      setReminderTime: (reminderTime) => {
        const timezone = currentTimezone();
        set({ reminderTime, timezone });
        syncFireAndForget(() =>
          api.me.updateNotificationPrefs({
            reminderEnabled: get().reminderEnabled,
            reminderTime,
            timezone,
          }),
        );
      },

      markOptInSeen: () => set({ optInPromptSeen: true }),

      hydrate: (prefs) =>
        set({
          reminderEnabled: prefs.reminderEnabled,
          reminderTime: prefs.reminderTime,
          timezone: prefs.timezone,
        }),

      reset: () => set({ ...initial }),
    }),
    {
      name: 'llura.notification-prefs',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
